/** CRM Data Aktarımı — Excel kolon eşleme ve yardımcılar */

export const DATA_TRANSFER_FIELDS = [
  { key: "tarih", label: "Tarih", excelKeys: ["TARİH", "TARIH", "Tarih", "tarih", "DATE"] },
  { key: "telefon", label: "Telefon", excelKeys: ["TELEFON", "Telefon", "telefon", "GSM", "TEL"] },
  { key: "ad_soyad", label: "İsim/Soyisim", excelKeys: ["İSİM/SOYİSİM", "ISIM/SOYISIM", "Ad Soyad", "ad_soyad", "İSİM", "ISIM"] },
  { key: "yas", label: "Yaş", excelKeys: ["YAŞ", "YAS", "Yaş", "yas", "YAS"] },
  { key: "boy", label: "Boy", excelKeys: ["BOY", "Boy", "boy"] },
  { key: "kilo", label: "Kilo", excelKeys: ["KİLO", "KILO", "Kilo", "kilo"] },
  { key: "belge", label: "Belge", excelKeys: ["BELGE", "Belge", "belge"] },
  { key: "hastalik", label: "Hastalık", excelKeys: ["HASTALIK", "Hastalık", "hastalik"] },
  { key: "notlar", label: "Notlar", excelKeys: ["NOTLAR", "Notlar", "notlar", "NOT"] },
  { key: "gorusme_sonucu", label: "Görüşme Sonucu", excelKeys: ["GÖRÜŞME SONUCU", "GORUSME SONUCU", "gorusme_sonucu", "SONUÇ", "SONUC"] },
  { key: "fiyat", label: "Fiyat", excelKeys: ["FİYAT", "FIYAT", "Fiyat", "fiyat", "TUTAR"] },
];

const normalizeHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/\s+/g, " ");

/** Excel hücre değerini API/önizleme için güvenli metne çevirir */
export const normalizeImportCellValue = (value, fieldKey = null) => {
  if (value === null || value === undefined || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    if (fieldKey === "tarih") return `${y}-${m}-${d}`;
    return `${d}.${m}.${y}`;
  }

  if (typeof value === "number") {
    if (fieldKey === "tarih" && value > 25569) {
      const utcDays = Math.floor(value - 25569);
      const date = new Date(utcDays * 86400 * 1000);
      const y = date.getUTCFullYear();
      const m = String(date.getUTCMonth() + 1).padStart(2, "0");
      const d = String(date.getUTCDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    }
    return value;
  }

  if (typeof value === "boolean") return value ? "1" : "0";
  if (typeof value === "object") return JSON.stringify(value);

  const text = String(value).trim();
  return text === "" ? null : text;
};

export const formatImportPreviewValue = (value, fieldKey = null) => {
  const normalized = normalizeImportCellValue(value, fieldKey);
  if (normalized === null || normalized === undefined || normalized === "") return "—";
  if (fieldKey === "tarih") return formatDataTransferDate(String(normalized));
  if (fieldKey === "fiyat") return formatDataTransferMoney(normalized);
  return String(normalized);
};

export const extractSheetHeaders = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const keys = new Set();
  rows.slice(0, 20).forEach((row) => {
    if (row && typeof row === "object") {
      Object.keys(row).forEach((key) => {
        if (key !== "__rowNum__") keys.add(key);
      });
    }
  });
  return Array.from(keys);
};

export const guessColumnMapping = (headers) => {
  const mapping = {};
  const headerLookup = {};
  headers.forEach((header) => {
    headerLookup[normalizeHeader(header)] = header;
  });

  DATA_TRANSFER_FIELDS.forEach(({ key, excelKeys }) => {
    mapping[key] = "";
    for (const alias of excelKeys) {
      const matched = headerLookup[normalizeHeader(alias)];
      if (matched) {
        mapping[key] = matched;
        break;
      }
    }
  });

  return mapping;
};

export const mapRowWithColumnMapping = (rawRow, columnMapping) => {
  if (!rawRow || typeof rawRow !== "object") return null;
  const mapped = {};

  DATA_TRANSFER_FIELDS.forEach(({ key }) => {
    const sourceHeader = columnMapping?.[key];
    if (!sourceHeader) return;
    const rawValue = rawRow[sourceHeader];
    mapped[key] = normalizeImportCellValue(rawValue, key);
  });

  return mapped;
};

export const applyColumnMappingToRows = (rawRows, columnMapping) =>
  (rawRows || [])
    .map((row) => mapRowWithColumnMapping(row, columnMapping))
    .filter((row) => row && !isDataTransferRowEmpty(row));

/** Eski otomatik eşleme — geriye dönük uyumluluk */
export const mapExcelRowToDataTransfer = (row) => {
  const headers = extractSheetHeaders([row]);
  const mapping = guessColumnMapping(headers);
  return mapRowWithColumnMapping(row, mapping);
};

export const isDataTransferRowEmpty = (row) => {
  if (!row) return true;
  return !row.telefon && !row.ad_soyad && !row.hastalik && !row.gorusme_sonucu;
};

export const formatDataTransferDate = (value) => {
  if (!value) return "—";
  if (value instanceof Date) {
    return formatImportPreviewValue(value, "tarih");
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) {
    const [y, m, d] = String(value).split("-");
    return `${d}.${m}.${y}`;
  }
  return String(value);
};

export const formatDataTransferMoney = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return num.toLocaleString("tr-TR", { minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

export const EMPTY_DATA_TRANSFER_FORM = {
  tarih: "",
  telefon: "",
  ad_soyad: "",
  yas: "",
  boy: "",
  kilo: "",
  belge: "",
  hastalik: "",
  notlar: "",
  gorusme_sonucu: "",
  fiyat: "",
  assigned_agent_id: "",
};

export const EMPTY_COLUMN_MAPPING = DATA_TRANSFER_FIELDS.reduce((acc, field) => {
  acc[field.key] = "";
  return acc;
}, {});
